import { Page, RelativeDateTime } from 'corso-admin-ui';
import { isTruthy } from 'corso-types';
import { PropsWithChildren, useState } from 'react';
import { useNavigateSearchData } from '~/components/searchTable/useNavigateSearchData';
import {
  isUndecided,
  useClaim,
  useIsLocked,
} from '~/features/claimReview/ClaimReviewProvider';
import { useIsConciergeWidget } from '~/hooks/widgetHooks';
import { useIsWidgetAuth } from '~/providers/AutoAuthProvider';
import CancelClaim from './claimActions/CancelClaim';
import FinalizeClaim from './claimActions/FinalizeClaim';
import RequestMoreInfo from './claimActions/RequestMoreInfo';
import RestoreWarrantyClaim from './claimActions/RestoreWarrantyClaim';

export default function ClaimPage({ children }: PropsWithChildren) {
  const claim = useClaim();
  const isWidgetAuth = useIsWidgetAuth();
  const isCorsoConcierge = useIsConciergeWidget();

  const paginationActions = useNavigateSearchData({
    currentId: claim.id,
    kind: claim.claimType,
  });

  const [showFinalizeClaim, setShowFinalizeClaim] = useState(false);
  const [showRequestMoreInfo, setShowRequestMoreInfo] = useState(false);
  const [showCancelClaim, setShowCancelClaim] = useState(false);
  const [showRestoreClaim, setShowRestoreClaim] = useState(false);
  const isLocked = useIsLocked();

  const hasUndecidedReviewLineItems = claim.claimLineItems.some(isUndecided);

  const claimId = claim.id;

  return (
    <Page
      backAction={!isWidgetAuth}
      title={
        claim.linkToCustomerUi
          ? {
              content: `#${claim.externalId}`,
              to: claim.linkToCustomerUi,
              target: '_blank',
            }
          : `#${claim.externalId}`
      }
      subtitle={
        <>
          <p>{claim.claimRollup.name}</p>
          <p>
            Created <RelativeDateTime dateTime={claim.createdOn} full />
          </p>
        </>
      }
      primaryAction={
        !isLocked
          ? {
              content: 'Finalize',
              onAction: () => setShowFinalizeClaim(true),
              disabled: hasUndecidedReviewLineItems,
            }
          : undefined
      }
      paginationActions={isCorsoConcierge ? undefined : paginationActions}
      secondaryActions={[
        {
          id: 'more-actions',
          content: 'More Actions',
          actions: [
            {
              id: 'request-more-info',
              icon: 'email' as const,
              content: 'Request More Info',
              onAction: () => setShowRequestMoreInfo(true),
            },
            !isLocked && {
              id: 'cancel-claim',
              icon: 'close' as const,
              content: 'Cancel Claim',
              onAction: () => setShowCancelClaim(true),
            },
            isLocked &&
              claim.claimType === 'Warranty' &&
              claim.claimLineItems.some((cli) => cli.canceledOn) && {
                id: 'restore-claim',
                icon: 'undo' as const,
                content: 'Restore Cancelled Claim',
                onAction: () => setShowRestoreClaim(true),
              },
          ].filter(isTruthy),
        },
      ].filter(isTruthy)}
    >
      {children}
      <FinalizeClaim
        show={showFinalizeClaim}
        onClose={() => setShowFinalizeClaim(false)}
      />
      <RequestMoreInfo
        show={showRequestMoreInfo}
        onClose={() => setShowRequestMoreInfo(false)}
      />
      <CancelClaim
        claimId={claimId}
        show={showCancelClaim}
        onClose={() => setShowCancelClaim(false)}
      />
      <RestoreWarrantyClaim
        claimId={claimId}
        show={showRestoreClaim}
        onClose={() => setShowRestoreClaim(false)}
      />
    </Page>
  );
}
